import axiosClient from "utils/axios";
import type { ApiList, ApiOptionList, ApiResponse } from "types/api.types";
import type {
  ChecklistTemplate,
  ChecklistTemplateItem,
  ChecklistTemplateType,
  CreateChecklistTemplateItemRequest,
  CreateChecklistTemplateRequest,
  UpdateChecklistTemplateItemRequest,
} from "features/ChecklistTemplates/types/checklist-template.types";

const CHECKLIST_TEMPLATE_BASE = "/checklist-templates";
const CHECKLIST_TEMPLATE_ITEM_BASE = "/checklist-template-items";

export type ChecklistTemplateListParams = {
  page?: number;
  limit?: number;
  query?: string;
  search?: string;
  type?: ChecklistTemplateType | string;
  equipmentType?: string;
  isActive?: boolean;
};

function buildChecklistTemplateListQueryParams(
  input: ChecklistTemplateListParams,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  const setPositiveInt = (key: string, v: number | undefined) => {
    if (v != null && Number.isFinite(v) && Number(v) > 0) {
      params[key] = Number(v);
    }
  };
  const setNonEmpty = (key: string, v: string | undefined) => {
    if (v != null && String(v).trim() !== "") {
      params[key] = String(v).trim();
    }
  };
  setPositiveInt("page", input.page);
  setPositiveInt("limit", input.limit);
  setNonEmpty("query", input.query);
  setNonEmpty("search", input.search);
  setNonEmpty("type", input.type);
  setNonEmpty("equipmentType", input.equipmentType);
  if (typeof input.isActive === "boolean") {
    params.isActive = input.isActive;
  }
  return params;
}

function normalizeTemplateRows(data: unknown): ChecklistTemplate[] {
  if (Array.isArray(data)) return data as ChecklistTemplate[];
  if (data && typeof data === "object") {
    const rows = (data as { rows?: unknown }).rows;
    if (Array.isArray(rows)) return rows as ChecklistTemplate[];
  }
  return [];
}

function normalizeHasMore(data: unknown, page: number, limit: number): boolean {
  if (data && typeof data === "object") {
    const anyData = data as {
      hasMore?: unknown;
      totalPages?: unknown;
      currentPage?: unknown;
      rows?: unknown[];
    };
    if (typeof anyData.hasMore === "boolean") return anyData.hasMore;
    const totalPages = Number(anyData.totalPages);
    const currentPage = Number(anyData.currentPage);
    if (Number.isFinite(totalPages) && Number.isFinite(currentPage)) {
      return currentPage < totalPages;
    }
    if (Array.isArray(anyData.rows)) {
      return anyData.rows.length >= limit;
    }
  }
  return false;
}

export const getChecklistTemplates = async (
  input: ChecklistTemplateListParams = {},
) => {
  const response = await axiosClient.get<ApiResponse<ApiList<ChecklistTemplate[]>>>(
    CHECKLIST_TEMPLATE_BASE,
    { params: buildChecklistTemplateListQueryParams(input) },
  );
  return response.data;
};

export const getChecklistTemplateById = async (id: number) => {
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error("Invalid checklist template id");
  }
  const response = await axiosClient.get<ApiResponse<ChecklistTemplate>>(
    `${CHECKLIST_TEMPLATE_BASE}/${id}`,
  );
  return response.data;
};

export const createChecklistTemplate = async (
  body: CreateChecklistTemplateRequest,
) => {
  const response = await axiosClient.post<ApiResponse<ChecklistTemplate>>(
    CHECKLIST_TEMPLATE_BASE,
    body,
  );
  return response.data;
};

export const createChecklistTemplateItem = async (
  templateId: number,
  body: CreateChecklistTemplateItemRequest,
) => {
  if (!Number.isInteger(templateId) || templateId <= 0) {
    throw new Error("Invalid checklist template id");
  }
  /** Backend validates `items` as an array; send a single-element batch. */
  const response = await axiosClient.post<ApiResponse<unknown>>(
    `${CHECKLIST_TEMPLATE_BASE}/${templateId}/items`,
    { items: [body] },
  );
  return response.data;
};

export const updateChecklistTemplate = async (
  templateId: number,
  body: Partial<CreateChecklistTemplateRequest>,
) => {
  if (!Number.isInteger(templateId) || templateId <= 0) {
    throw new Error("Invalid checklist template id");
  }
  const response = await axiosClient.put<ApiResponse<ChecklistTemplate>>(
    `${CHECKLIST_TEMPLATE_BASE}/${templateId}`,
    body,
  );
  return response.data;
};

export const updateChecklistTemplateItem = async (
  itemId: number,
  body: UpdateChecklistTemplateItemRequest,
) => {
  if (!Number.isInteger(itemId) || itemId <= 0) {
    throw new Error("Invalid checklist template item id");
  }
  const response = await axiosClient.put<ApiResponse<ChecklistTemplateItem>>(
    `${CHECKLIST_TEMPLATE_ITEM_BASE}/${itemId}`,
    body,
  );
  return response.data;
};

export const deleteChecklistTemplateItem = async (itemId: number) => {
  if (!Number.isInteger(itemId) || itemId <= 0) {
    throw new Error("Invalid checklist template item id");
  }
  const response = await axiosClient.delete<ApiResponse<unknown>>(
    `${CHECKLIST_TEMPLATE_ITEM_BASE}/${itemId}`,
  );
  return response.data;
};

export const getChecklistTemplateOptions = async ({
  query,
  limit = 20,
  page = 1,
  type,
  isActive,
}: {
  query?: string;
  limit?: number;
  page?: number;
  type?: ChecklistTemplateType | string;
  isActive?: boolean;
}) => {
  const res = await getChecklistTemplates({
    query,
    page,
    limit,
    type,
    isActive,
  });
  const rows = normalizeTemplateRows(res.data);
  const hasMore = normalizeHasMore(res.data, page, limit);
  const mapped = rows.map((r) => ({
    value: r.id,
    label: r.name?.trim() || `Template #${r.id}`,
  }));
  return {
    success: res.success,
    message: res.message,
    data: {
      rows: mapped,
      hasMore,
    } as ApiOptionList,
  };
};
